// The ink: the selection mark that moves instead of appearing.
//
// A strip of tabs and a list of conversations both say «this one» by filling
// the chosen child. Painted as that child's own `background`, the mark can only
// blink from one child to the next — there is no single thing on screen to
// animate. So it is painted once instead: one element parked behind the row,
// which rolls from the child that was chosen to the one that is.
//
// Nothing here animates a length or a position. The script writes numbers into
// custom properties and `app.css` spends them on `transform`s alone, which is
// the whole reason the measuring is done in JavaScript at all — a strip inside
// a scroller inside a blurred pane cannot afford a relayout per frame, and only
// the page knows how wide «Channels» came out in the font and the language it
// ended up with.
//
// Both clients supply markup and nothing else:
//
//     <div data-ink>                    the host — whatever holds the choices
//       <span class="echo-ink"></span>  the mark, always the host's own child
//       <button data-ink-here>…</button>  the chosen child
//       <button>…</button>
//     </div>
//
// and get back five properties on the mark — `--ink-x`/`--ink-y` where the
// chosen child stands, `--ink-w`/`--ink-h` how big it is, and `--ink-bar`, the
// scale of the middle segment of the three the widening capsule is drawn out of
// (`app.css` carries that geometry) — plus `.echo-ink--placed` once those numbers are
// real. A host with nothing chosen loses the class and the mark goes away,
// which is the same nothing the old per-child background drew.
//
// The pass is driven by observers rather than by a call from either client: a
// framework that re-renders a list has no good moment to say «and now measure»,
// and both of them mark the chosen child with the same attribute this file
// already watches for.
(function () {
  "use strict";

  // One observer owner for this document, including repeated script/HMR loads.
  if (window.__echoInkInstalled) return;
  window.__echoInkInstalled = true;

  var HOST = "[data-ink]";
  var HERE = "[data-ink-here]";

  // The hosts whose size is being watched. An array rather than a set because
  // it is also the list that gets swept: a host that has left the document —
  // the sidebar's list is rebuilt whole on every tab switch — must be let go of
  // or the observer holds it for the life of the window.
  var watched = [];
  var sizes = null;
  var queued = false;

  function px(value) {
    return Math.round(value * 100) / 100 + "px";
  }

  function place(host) {
    var ink = host.querySelector(":scope > .echo-ink");
    if (ink === null) return;

    var target = host.querySelector(HERE);
    if (target === null) {
      // Nothing is chosen. Dropping the class takes the mark off screen *and*
      // takes its transition with it, so that whatever is chosen next is an
      // appearance rather than a journey from wherever the mark was left.
      ink.classList.remove("echo-ink--placed");
      return;
    }

    var base = host.getBoundingClientRect();
    var box = target.getBoundingClientRect();
    // Not laid out yet — a hidden host, or a pass that landed between the mount
    // and the first layout. Leaving the mark unplaced is the honest answer;
    // the observers will be back the moment there is a box to measure.
    if (box.width === 0 || box.height === 0) return;

    // Into the host's own content coordinates: the mark is a child of the host
    // and scrolls with it, so what is wanted is the distance from the top-left
    // of everything the host holds, not from the top-left of what shows.
    // Rects are on screen, and under a `zoom` (the composer's emoji card,
    // dioxus-plan.md D683) a length written back into the host is zoomed
    // again — so they are divided back into the host's own pixels, which is
    // what `scrollLeft` already is.
    var zoom = host.currentCSSZoom || 1;
    var w = box.width / zoom;
    var h = box.height / zoom;
    var x = (box.left - base.left) / zoom + host.scrollLeft;
    var y = (box.top - base.top) / zoom + host.scrollTop;

    var style = ink.style;
    style.setProperty("--ink-x", px(x));
    style.setProperty("--ink-y", px(y));
    style.setProperty("--ink-w", px(w));
    style.setProperty("--ink-h", px(h));
    // The middle segment of the capsule: the width less the two half-round
    // caps, plus the pixel each end overlaps its cap by, as a scale of the
    // bar's 100px base (`app.css` says why it is not 1px). Unitless — `scaleX`
    // takes a number, and CSS cannot divide one length by another. Kept to a
    // hundredth of a pixel, which is four places of the scale.
    style.setProperty(
      "--ink-bar",
      String(Math.max(0, Math.round((w - h + 2) * 100) / 10000)),
    );

    if (!ink.classList.contains("echo-ink--placed")) {
      // The first placement must not be a roll out of the corner. Reading a
      // layout property commits the transform above while the class that
      // carries the transition is still off; adding it afterwards has nothing
      // left to animate.
      void ink.offsetWidth;
      ink.classList.add("echo-ink--placed");
    }
  }

  function pass() {
    queued = false;

    for (var i = watched.length - 1; i >= 0; i--) {
      if (!watched[i].isConnected) {
        sizes.unobserve(watched[i]);
        watched.splice(i, 1);
      }
    }

    var hosts = document.querySelectorAll(HOST);
    for (var j = 0; j < hosts.length; j++) {
      if (watched.indexOf(hosts[j]) < 0) {
        watched.push(hosts[j]);
        sizes.observe(hosts[j]);
      }
      place(hosts[j]);
    }
  }

  // One pass per frame however many records arrive: a tab switch rebuilds a
  // whole list, and measuring once per mutation would be measuring a tree that
  // is still half old.
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(pass);
  }

  function start() {
    sizes = new ResizeObserver(schedule);
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only the one attribute. `class` would do as well and would also wake
      // this on every speaking ring in a call, five times a second, for a
      // question whose answer cannot have changed.
      attributeFilter: ["data-ink-here"],
    });
    // A face that arrives late re-measures every label under it.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule);
    }
    schedule();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
